import { useEffect } from "react";
import { useQueryClient } from "@tanstack/react-query";

const REFRESH_INTERVAL_MS = 30_000;

/**
 * Recovery path for shared boards when SSE was interrupted or a browser tab
 * slept through an update. SSE remains the fast path; this hook guarantees
 * eventual reconciliation without polling hidden tabs.
 */
export function useBoardCardRefresh(launchId: string | null | undefined): void {
  const queryClient = useQueryClient();

  useEffect(() => {
    if (!launchId) return;

    const refresh = () => {
      if (document.visibilityState !== "visible") return;
      void queryClient.invalidateQueries({ queryKey: ["cards", launchId] });
    };
    const onVisibilityChange = () => refresh();

    window.addEventListener("focus", refresh);
    window.addEventListener("online", refresh);
    document.addEventListener("visibilitychange", onVisibilityChange);
    const intervalId = window.setInterval(refresh, REFRESH_INTERVAL_MS);

    return () => {
      window.removeEventListener("focus", refresh);
      window.removeEventListener("online", refresh);
      document.removeEventListener("visibilitychange", onVisibilityChange);
      window.clearInterval(intervalId);
    };
  }, [launchId, queryClient]);
}
